import * as React from "react";
import {
  makeStyles,
  Textarea,
  Button,
  Field,
  Switch,
  Input,
  Spinner,
  buttonClassNames,
  tokens,
} from "@fluentui/react-components";
import { CheckmarkFilled } from "@fluentui/react-icons";

/**
 * Interface définissant les propriétés du composant ChatPage.
 */
interface ChatProps {
  handleMenuReload: () => void; // Fonction pour recharger le menu principal
  respondToCasePrompt: (prompt: string, targetCase: string) => void; // Fonction pour exécuter une requête de génération de formule
}

/**
 * Fonction utilitaire pour convertir une chaîne de lettres en un nombre.
 * @param str La chaîne de lettres à convertir.
 * @returns Le nombre correspondant à la chaîne de lettres.
 */
function convertLettersToNumber(str: String) {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  let result = 0;
  const reversedStr = str.toUpperCase().split("").reverse();
  for (let i = 0; i < reversedStr.length; i++) {
    const char = reversedStr[i];
    const position = alphabet.indexOf(char) + 1; // +1 pour que A=1, B=2, etc.
    result += position * Math.pow(10, i); // Utilisation de la puissance de 10 pour le positionnement
  }
  return result;
}

/**
 * Composant ChatPage : permet à l'utilisateur de poser des questions à une IA
 * et de spécifier où afficher la réponse (case sélectionnée ou case spécifiée).
 * @param {ChatProps} props - Propriétés du composant.
 * @returns {JSX.Element} - Le composant React.
 */
const ChatPage: React.FC<ChatProps> = (props: ChatProps) => {
  const styles = useStyles();

  // États locaux du composant
  const [checked, setChecked] = React.useState(true); // État pour savoir si la case est sélectionnée ou non
  const [question, setQuestion] = React.useState(""); // État pour stocker la question posée par l'utilisateur
  const [toCell, setToCell] = React.useState(""); // État pour stocker la case où afficher la réponse
  const [loadingState, setLoadingState] = React.useState<LoadingState>("initial"); // État pour gérer le chargement de la réponse
  type LoadingState = "initial" | "loading" | "loaded"; // Type pour représenter les états de chargement
  const [isDisabled, setIsDisabled] = React.useState(true); // État pour désactiver le bouton d'envoi si les entrées ne sont pas valides
  const [errorMessage, setErrorMessage] = React.useState<string>(""); // État pour stocker les messages d'erreur

  // Contenu et icône du bouton en fonction de l'état de chargement
  const buttonContent =
    loadingState === "loading" ? "En cours..." : loadingState === "loaded" ? "Terminé" : "Envoyer";

  const buttonIcon =
    loadingState === "loading" ? (
      <Spinner size="tiny" />
    ) : loadingState === "loaded" ? (
      <CheckmarkFilled />
    ) : null;

  /**
   * Valide les entrées utilisateur (question et case cible).
   */
  const validateEntries = () => {
    const cellRegex = /^([A-Z]{1,3})([1-9]{1}[0-9]{0,6})$/;
    if (question.trim() === "") {
      setIsDisabled(true);
    } else if (question.trim().length < 7) {
      setErrorMessage(
        "Veuillez entrer au moins 7 caractères. L'entrée ne peut pas être vide ni contenir moins de 7 caractères."
      );
      setIsDisabled(true);
    } else if (!checked && toCell == "") {
      setErrorMessage("Veuillez saisir une cellule où afficher le résultat.");
      setIsDisabled(true);
    } else if (!checked && !toCell.match(cellRegex)) {
      setErrorMessage("Veuillez saisir une cellule valide où afficher le résultat.");
      setIsDisabled(true);
    } else {
      setErrorMessage("");
      setIsDisabled(false);
    }
  };

  /**
   * Gère le changement de l'état du switch (case sélectionnée ou spécifiée).
   */
  const onChange = () => {
    setChecked(!checked);
    setToCell("");
  };

  // Valide les entrées à chaque mise à jour des états pertinents
  React.useEffect(() => {
    validateEntries();
  });

  /**
   * Gère les changements dans le champ de saisie de la cellule cible.
   * Valide et ajuste la valeur saisie pour respecter les limites d'Excel.
   * @param {React.ChangeEvent<HTMLInputElement>} e - L'événement de changement.
   */
  const handleCellChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();
    const cellRegex = /^([A-Z]{1,3})([1-9][0-9]{0,6})?$/;
    const match = value.match(cellRegex);

    if (match) {
      let column = match[1];
      let row = match[2] ? parseInt(match[2]) : NaN;

      const columnNumber = convertLettersToNumber(column);
      const maxColumnNumber = convertLettersToNumber("XFD"); // Limite de colonne d'Excel

      if (columnNumber > maxColumnNumber) {
        column = "XFD";
      }

      if (!isNaN(row)) {
        row = Math.min(Math.max(row, 1), 1048576); // Limite de ligne d'Excel
      }

      value = `${column}${row || ""}`;
      setToCell(value);
    } else setToCell("");
  };

  /**
   * Envoie la question à l'IA et gère la réponse.
   */
  const getAnswer = async () => {
    const targetCell = checked ? "-1" : toCell; // Si la case est sélectionnée, on utilise "-1" comme valeur par défaut
    setLoadingState("loading");
    await props.respondToCasePrompt(question, targetCell);
    setLoadingState("loaded");
    setTimeout(() => {
      setLoadingState("initial");
      props.handleMenuReload(); // Recharge le menu principal après un délai de 1,2 seconde
    }, 1200);
  };

  // Rendu du composant
  return (
    <div className={styles.container}>
      {/* Titre du composant */}
      <p className={styles.text}>Générateur de formule</p>
      {/* Champ de saisie pour la question */}
      <Field label="Question" className={styles.fieldPadding}>
        <Textarea
          placeholder="Ajoute b1 et b2"
          size="medium"
          resize="none"
          rows={3}
          onChange={(e: any) => {
            setQuestion(e.target.value);
          }}
        />
        {errorMessage && <p className={styles.errorContainer}>{errorMessage}</p>}
      </Field>
      {/* Bouton pour sélectionner la case cible */}
      {!checked && (
        <Field label="Case réponse">
          <Field className={styles.fieldFlexColumn}>
            <Input
              placeholder="A1"
              size="medium"
              className={styles.inputWidth}
              value={toCell}
              onChange={handleCellChange}
            />
          </Field>
        </Field>
      )}
      {/* Champ de sélection pour choisir entre la case sélectionnée ou spécifiée */}
      <Field className={styles.fieldAlignLeft}>
        <Switch
          checked={checked}
          onChange={onChange}
          label={
            checked ? "Réponse ira dans la case selectionné" : "Réponse ira dans la case inscrite"
          }
        />
        {/* Bouton submit */}
        <Button
          appearance="primary"
          disabled={isDisabled}
          className={`${styles.greenButton}`}
          size="medium"
          onClick={getAnswer}
          icon={buttonIcon}
        >
          {" "}
          {buttonContent}
        </Button>
      </Field>
    </div>
  );
};

export default ChatPage;

const useStyles = makeStyles({
  container: {
    padding: "20px",
    textAlign: "center",
    flexDirection: "column",
  },
  text: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  fieldPadding: {
    paddingBottom: "12px",
  },
  fieldAlignLeft: {
    paddingBottom: "12px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    justifyContent: "flex-start",
    gap: "8px",
  },
  fieldFlexColumn: {
    display: "flex",
    flexDirection: "row",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    paddingBottom: "12px",
  },
  buttonAutoWidth: {
    width: "auto",
    height: "auto",
    padding: "8px 16px",
  },
  buttonNonInteractive: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground1,
    cursor: "default",
    pointerEvents: "none",

    [`& .${buttonClassNames.icon}`]: {
      color: tokens.colorStatusSuccessForeground1,
    },
  },
  greenButton: {
    backgroundColor: "#217346",
    color: "white",
    "&:hover": {
      backgroundColor: "#1e6d43",
    },
  },
  inputWidth: {
    width: "110px",
  },
  errorContainer: {
    display: "flex",
    alignItems: "center",
    color: "red",
  },
});
